import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsApi, type NotificationRecord } from '@/api/client';
import { useAsync } from '@/hooks';
import { useToast } from './Feedback';
import { formatDateTime } from '@/lib/format';

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: unread, loading, error, reload } = useAsync(() => notificationsApi.list(true), []);
  const toast = useToast();
  const count = unread?.length ?? 0;

  const toggle = () => {
    setOpen((wasOpen) => {
      if (!wasOpen) reload();
      return !wasOpen;
    });
  };

  const handleItemClick = async (n: NotificationRecord) => {
    try {
      await notificationsApi.markRead(n.id);
    } catch {
      // Opening the notification matters more than the read flag; it stays unread.
    } finally {
      reload();
      setOpen(false);
      if (n.deepLink) navigate(n.deepLink);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      reload();
    } catch (err) {
      toast((err as { message?: string }).message ?? 'Couldn\'t mark notifications as read. Please try again.', 'error');
    }
  };

  return (
    <div className="notif-bell">
      <button
        type="button"
        className="notif-bell-trigger"
        onClick={toggle}
        aria-label={`Notifications${count ? ` (${count} unread)` : ''}`}
        aria-expanded={open}
      >
        <span aria-hidden="true">🔔</span>
        {count > 0 && <span className="notif-bell-badge">{count > 9 ? '9+' : count}</span>}
      </button>

      {open && (
        <>
          <div className="notif-bell-scrim" onClick={() => setOpen(false)} />
          <div className="notif-bell-dropdown" role="menu">
            <div className="notif-bell-header">
              <strong>Notifications</strong>
              <div className="row" style={{ gap: '0.5rem' }}>
                {count > 0 && (
                  <button type="button" className="notif-bell-link" onClick={handleMarkAllRead}>
                    Mark all read
                  </button>
                )}
                <button
                  type="button"
                  className="notif-bell-link"
                  onClick={() => {
                    setOpen(false);
                    navigate('/notifications');
                  }}
                >
                  See all
                </button>
              </div>
            </div>
            {loading ? (
              <p className="notif-bell-empty">Loading…</p>
            ) : error ? (
              <p className="notif-bell-empty">
                Notifications couldn&apos;t be loaded.{' '}
                <button type="button" className="notif-bell-link" onClick={() => reload()}>Retry</button>
              </p>
            ) : !unread?.length ? (
              <p className="notif-bell-empty">No unread notifications.</p>
            ) : (
              <ul className="notif-bell-list">
                {unread.slice(0, 12).map((n) => (
                  <li key={n.id}>
                    <button type="button" onClick={() => handleItemClick(n)}>
                      <span className="notif-bell-subject">{n.subject}</span>
                      <span className="notif-bell-meta">
                        {formatDateTime(n.createdAt)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      <style>{`
        .notif-bell { position: relative; }
        .notif-bell-trigger {
          position: relative;
          background: transparent;
          border: none;
          color: inherit;
          font-size: 1.125rem;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: var(--radius-sm);
        }
        .notif-bell-trigger:hover { background: rgba(255,255,255,0.08); }
        .notif-bell-badge {
          position: absolute;
          top: -2px;
          right: -2px;
          background: var(--color-urgent, #C83C32);
          color: white;
          border-radius: 999px;
          font-size: 0.625rem;
          font-weight: 700;
          line-height: 1;
          padding: 3px 5px;
          min-width: 16px;
        }
        .notif-bell-scrim {
          position: fixed;
          inset: 0;
          z-index: 40;
        }
        .notif-bell-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          width: min(320px, calc(100vw - 1.5rem));
          max-width: calc(100vw - 1.5rem);
          max-height: 420px;
          overflow: auto;
          background: var(--color-surface);
          color: var(--color-text);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          box-shadow: 0 12px 32px rgba(0,0,0,0.18);
          z-index: 41;
        }
        .notif-bell-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-sm) var(--space-md);
          border-bottom: 1px solid var(--color-border);
        }
        .notif-bell-link {
          background: none;
          border: none;
          color: var(--color-primary, #2E5266);
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          padding: 0;
        }
        .notif-bell-empty {
          padding: var(--space-lg) var(--space-md);
          font-size: 0.8125rem;
          color: var(--color-text-muted);
          text-align: center;
        }
        .notif-bell-list { list-style: none; margin: 0; padding: 0; }
        .notif-bell-list li { border-bottom: 1px solid var(--color-border); }
        .notif-bell-list li:last-child { border-bottom: none; }
        .notif-bell-list button {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 2px;
          width: 100%;
          text-align: left;
          background: none;
          border: none;
          cursor: pointer;
          padding: var(--space-sm) var(--space-md);
        }
        .notif-bell-list button:hover { background: var(--color-fog); }
        .notif-bell-subject { font-size: 0.8125rem; font-weight: 600; color: var(--color-text); }
        .notif-bell-meta { font-size: 0.6875rem; color: var(--color-text-muted); }
      `}</style>
    </div>
  );
}
